import {
  Box,
  Button,
  Divider,
  FormControl,
  Option,
  Select,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import { t } from "i18next";
import { BrowserRouterConstants } from "../../../utils/constants";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { theme } from "../../../components/Theme";
import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid";
import TablePagination from "../../Inventory/components/TablePagination";
import { PaginationType } from "../../../types/item";
import { Trans } from "react-i18next";
import { MyLimit, MyTransactionType } from "../../../types/buy-now-paylater";
import { BuyNowPaylaterService } from "../../../services/buy-now-paylater.service";
import { formatCurrencyByCode } from "../../../utils/formatPrice";
import moment from "moment";
import { getActiveBusiness } from "../../../store/session";

export default function LandingPage() {
  const [myTransactions, setMyTransactions] = useState<MyTransactionType[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    limit: 10,
    max: 1,
  });
  const [checkedStatus, setCheckStatus] = useState("");
  const [timeFilter, setTimeFilter] = useState<string>("");

  const [myLimit, setMyLimit] = useState<MyLimit>();

  const fetchMyLimit = async () => {
    try {
      const response = await BuyNowPaylaterService.getMyLimit();
      if (response) {
        setMyLimit(response);
      }
    } catch (error) {
      console.error("Error fetching limit:", error);
    }
  };

  const fetchMyTransaction = async ({
    paging = true,
    limit = 10,
    offset = 0,
    sortBy = "updatedAt",
    sort = "desc",
    timePeriod,
  }: {
    paging?: boolean;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sort?: "asc" | "desc";
    timePeriod?: string | null;
  }) => {
    try {
      const res = await BuyNowPaylaterService.getTransactions({
        paging,
        limit,
        offset,
        sortBy,
        sort,
        timePeriod,
      });

      if (res?.data) {
        setMyTransactions(res.data);
      }

      if (res?.pagination) {
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  useEffect(() => {
    fetchMyLimit();
    fetchMyTransaction({ paging: true, limit: pagination.limit, offset: 0 });
  }, []);

  useEffect(() => {
    const period = searchParams.get("timePeriod");
    const _sortDirection = searchParams.get("sort");
    if (period) {
      setTimeFilter(period);
    }
    if (_sortDirection) {
      setSort(_sortDirection as any);
    }

    return () => {};
  }, []);

  useEffect(() => {
    const businessId = getActiveBusiness()?.id;

    if (businessId) {
      fetchMyTransaction({
        paging: true,
        limit: pagination.limit,
        offset: 0,
        sortBy: "updatedAt",
        sort: sort,
        timePeriod: timeFilter,
      });
      const newPagination = pagination;
      newPagination.limit = 10;
      setPagination(newPagination);
      handlePaging("first");
    }
  }, [timeFilter, sort]);

  function checkPaymentStatus(dateString: string) {
    const dueDate = moment(dateString).startOf("day");
    const today = moment().startOf("day");
    const dueSoonStart = dueDate.clone().subtract(7, "days");

    if (today.isAfter(dueDate)) {
      setCheckStatus("Late Payment");
      return "Late Payment";
    } else if (today.isBetween(dueSoonStart, dueDate, null, "[]")) {
      setCheckStatus("Due Soon");
      return "Due Soon";
    } else if (today.isBefore(dueSoonStart)) {
      setCheckStatus("Due Payment");
      return "Due Payment";
    } else {
      setCheckStatus("UNPAID");
      return "UNPAID";
    }
  }

  const BizPayLater = () => {
    return (
      <>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          paddingY={{ xs: 0, lg: 2 }}
          gap={{ xs: 2, lg: 3 }}
        >
          {myLimit && myLimit.isActive ? (
            <Stack spacing={2} width={{ xs: "100%", lg: "49%" }}>
              <Typography fontSize={16} fontWeight={600} textColor={"#101828"}>
                {t("finance.activatedLanding.bizPayLater")}
              </Typography>
              <div>
                <Typography fontSize={12} fontWeight={400}>
                  {t("finance.activatedLanding.remainingCredit")}
                </Typography>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  alignItems={{ xs: "flex-start", lg: "center" }}
                  gap={1}
                  paddingTop={1}
                >
                  <span className="text-2xl font-bold">
                    {formatCurrencyByCode(
                      myLimit?.remainingLimit ?? 0,
                      myLimit?.currency ?? "IDR"
                    )}
                  </span>
                  <span className="text-[16px] font-normal text-[#98A2B3] pt-1">
                    {"/ "}
                    {formatCurrencyByCode(
                      myLimit?.startingLimit ?? 0,
                      myLimit?.currency ?? "IDR"
                    )}
                  </span>
                </Stack>
              </div>
            </Stack>
          ) : (
            <Stack spacing={2} width={{ xs: "100%", lg: "49%" }}>
              <Typography fontSize={16} fontWeight={600} textColor={"#101828"}>
                {t("finance.activatedLanding.bizPayLater")}
              </Typography>
              <div>
                <Typography paddingTop={1}>
                  <span className="text-2xl font-bold text-[#98A2B3]">
                    {"~ IDR 20.000.000"}{" "}
                  </span>
                </Typography>
                <Typography fontSize={12} fontWeight={400} paddingTop={1}>
                  <Trans
                    i18nKey="finance.deactivatedLanding.activateCredit"
                    values={{ currency: "IDR", amount: "20.000.000" }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </div>
            </Stack>
          )}

          <Divider
            orientation="vertical"
            sx={{ display: { xs: "none", lg: "block" } }}
          />
          <Divider sx={{ display: { xs: "block", lg: "none" } }} />
          <Stack spacing={2} width={{ xs: "100%", lg: "49%" }}>
            <Typography fontSize={16} fontWeight={600} textColor={"#101828"}>
              {t("finance.activatedLanding.totalBills")}
            </Typography>
            <div className="">
              <Typography>
                <span className="text-2xl font-bold">
                  {myLimit?.totalBills && myLimit.totalBills > 0
                    ? formatCurrencyByCode(
                        myLimit?.totalBills,
                        myLimit?.currency ?? "IDR"
                      )
                    : `${myLimit?.currency} 0`}
                </span>
              </Typography>
              {myLimit?.totalBills &&
              myLimit.totalBills > 0 &&
              myLimit.lastBillsDueDate != null ? (
                <Typography fontSize={12} paddingTop={1}>
                  <span className="font-normal">
                    {" "}
                    {t("finance.activatedLanding.totalBillText")}
                  </span>
                  <span className="font-bold">
                    {" "}
                    {moment(myLimit.lastBillsDueDate).format(
                      "DD MMM YYYY hh:mm A"
                    )}
                  </span>
                </Typography>
              ) : (
                <Typography fontSize={12} paddingTop={1}>
                  <span className="font-normal">
                    {" "}
                    {t("finance.activatedLanding.noTotalBillText")}
                  </span>
                  <span className="font-bold">
                    {" "}
                    {t("finance.activatedLanding.bizPayLater")}
                  </span>
                </Typography>
              )}
            </div>
          </Stack>
        </Stack>
        <Divider />

        <Typography fontSize={16} fontWeight={600} textColor={"#101828"}>
          {t("finance.activatedLanding.billingList")}
        </Typography>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <FormControl size="sm">
            <Select
              onChange={(
                _event: React.SyntheticEvent | null,
                newValue: string | null
              ) => {
                if (newValue || newValue === "") {
                  const newParams = new URLSearchParams(searchParams);

                  if (newValue === "") {
                    newParams.delete("date", encodeURIComponent(newValue));
                  } else {
                    newParams.set("date", encodeURIComponent(newValue));
                  }

                  setSearchParams(newParams);
                }
              }}
              slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
              size="sm"
              defaultValue={""}
              sx={{
                paddingY: "10px",
                paddingX: "16px",
                fontWeight: 600,
                gap: "8px",
                borderRadius: "8px",
                color: theme.colorSchemes.light.palette.others.neutral_dark,
                borderColor:
                  theme.colorSchemes.light.palette.others.neutral_dark,
              }}
              indicator={<ChevronDownIcon width={18} height={18} />}
            >
              <Option value={""}>
                {t("finance.activatedLanding.allFilterBtn")}
              </Option>
              <Option value="this_week">
                {t("order.filters.date.thisWeek")}
              </Option>
              <Option value="this_month">
                {t("order.filters.date.thisMonth")}
              </Option>
              <Option value="this_year">
                {t("order.filters.date.thisYear")}
              </Option>
            </Select>
          </FormControl>
          <Button
            variant="filter-outlined"
            endDecorator={
              sort === "asc" ? (
                <BarsArrowUpIcon width={18} height={18} />
              ) : (
                <BarsArrowDownIcon width={18} height={18} />
              )
            }
            className="md:min-w-[10.8125rem] min-w-[10px] whitespace-nowrap rounded-lg"
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set("sort", sort === "asc" ? "desc" : "asc");
              setSearchParams(newParams);
              setSort(sort === "asc" ? "desc" : "asc");
            }}
          >
            <span className="hidden md:block">
              {t(
                `catalog.inventory.sort${
                  sort === "asc" ? "Ascending" : "Descending"
                }Btn`
              )}
            </span>
          </Button>
        </Stack>
      </>
    );
  };

  const BillingTable = () => {
    return (
      <>
        <Box
          sx={{
            width: { xs: "90vw", lg: "78vw" },
            height: "100%",
            maxHeight: { lg: "27.5rem" },
            overflow: "auto",
            borderWidth: { lg: "2px" },
            my: "0.75rem",
            display: { xs: "none", lg: "flex" },
          }}
        >
          <Table
            borderAxis="x"
            sx={{
              "& th": { textAlign: "center" },
              minWidth: { xs: "1000px", md: "100%" },
            }}
          >
            <thead>
              <tr key={"header"}>
                <th key={"orderNo"} className="w-[40%]">
                  {t("finance.activatedLanding.orderNo")}
                </th>
                <th key={"date"} className="w-[30%]">
                  {t("finance.activatedLanding.date")}
                </th>
                <th key={"term"} className="w-[20%]">
                  {t("finance.activatedLanding.term")}
                </th>
                <th key={"payLaterAmount"} className="w-[30%]">
                  {t("finance.activatedLanding.payLaterAmount")}
                </th>
                <th key={"dueDate"} className="w-[40%]">
                  {t("finance.activatedLanding.dueDate")}
                </th>
                <th key={"repaymentAmount"} className="w-[30%]">
                  {t("finance.activatedLanding.repaymentAmount")}
                </th>
                <th key={"status"} className="w-[30%]">
                  {t("finance.activatedLanding.status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {myTransactions &&
                myTransactions.map((transaction, index) => {
                  return (
                    <tr key={index}>
                      <td className="text-center break-all">
                        {transaction.orderNumber}
                      </td>
                      <td className="text-center break-all">
                        {moment(transaction.createdAt).format("DD MMM YYYY")}
                      </td>
                      <td className="text-center break-all">
                        {transaction.repaymentPeriod}
                        {" Days"}
                      </td>
                      <td className="text-center break-all">
                        {formatCurrencyByCode(
                          transaction.amount,
                          transaction.currency
                        )}
                      </td>
                      <td className="text-center break-all">
                        {moment(transaction.repaymentDueDate).format(
                          "DD MMM YYYY hh:mm A"
                        )}
                      </td>
                      <td className="text-center break-all">
                        {formatCurrencyByCode(
                          transaction.repaymentAmount,
                          transaction.currency
                        )}
                      </td>
                      <td className="text-center break-all">
                        <span
                          className={`px-2 py-1 rounded-[40px] ${
                            checkedStatus === "Due Payment" &&
                            "bg-[#EBE1F7] text-[#8E30E2]"
                          } ${
                            checkedStatus === "Due Soon" &&
                            "bg-[#FCE1C2] text-[#904418]"
                          } ${
                            checkedStatus === "Late Payment" &&
                            "bg-[#F3CFCE] text-[#C31D1D]"
                          } ${
                            checkedStatus === "UNPAID" &&
                            "bg-[#F3CFCE] text-[#C31D1D]"
                          } `}
                        >
                          {checkPaymentStatus(transaction.repaymentDueDate)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </Box>

        <Box
          sx={{
            width: { xs: "90vw", lg: "78vw" },
            height: "100%",
            maxHeight: { lg: "27.5rem" },
            overflow: "auto",
            borderWidth: { lg: "2px" },
            my: "0.75rem",
            display: { xs: "flex", lg: "none" },
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {myTransactions &&
            myTransactions.map((transaction, index) => {
              return (
                <Stack
                  key={index}
                  gap={1}
                  sx={{
                    padding: "16px",
                    border: 1,
                    borderColor: "#CDD7E1",
                    borderRadius: "16px",
                  }}
                >
                  <Typography fontSize={14} fontWeight={600}>
                    {transaction.transactionId}
                  </Typography>
                  <Typography fontSize={20} fontWeight={700}>
                    {transaction.repaymentAmount}
                  </Typography>
                  <Typography fontSize={14} fontWeight={500}>
                    <span
                      className={`px-2 py-1 rounded-[40px] ${
                        checkedStatus === "Due Payment" &&
                        "bg-[#EBE1F7] text-[#8E30E2]"
                      } ${
                        checkedStatus === "Due Soon" &&
                        "bg-[#FCE1C2] text-[#904418]"
                      } ${
                        checkedStatus === "Late Payment" &&
                        "bg-[#F3CFCE] text-[#C31D1D]"
                      } ${
                        checkedStatus === "UNPAID" &&
                        "bg-[#F3CFCE] text-[#C31D1D]"
                      } `}
                    >
                      {checkPaymentStatus(transaction.repaymentDueDate)}
                    </span>
                  </Typography>
                  <Typography fontSize={14}>
                    <span className="font-semibold text-[#171A1C]">
                      {t("finance.activatedLanding.payLaterAmount")}
                      {": "}
                    </span>
                    <span className="font-normal text-[#171A1C]">
                      {transaction.amount}
                    </span>
                    <span></span>
                  </Typography>
                  <Typography fontSize={14}>
                    <span className="font-semibold text-[#171A1C]">
                      {t("finance.activatedLanding.term")}
                      {": "}
                    </span>
                    <span className="font-normal text-[#171A1C]">
                      {transaction.repaymentPeriod} {" Days"}
                    </span>
                    <span></span>
                  </Typography>
                  <Typography fontSize={14}>
                    <span className="font-semibold text-[#171A1C]">
                      {t("finance.activatedLanding.dueDate")}
                      {": "}
                    </span>
                    <span className="font-normal text-[#171A1C]">
                      {moment(transaction.repaymentDueDate).format(
                        "DD MMM YYYY hh:mm A"
                      )}
                    </span>
                    <span></span>
                  </Typography>
                </Stack>
              );
            })}
          <Stack></Stack>
        </Box>

        <TablePagination
          handlePaging={handlePaging}
          pagination={pagination}
          setPagination={setPagination}
          withLimit
        />
      </>
    );
  };

  const handlePaging = (action: string | number) => {
    const pagingConfig = {
      limit: pagination.limit,
      offset: 0,
    };

    if (typeof action === "string") {
      switch (action) {
        case "first":
          pagingConfig.offset = 0;
          break;

        case "previous":
          pagingConfig.offset = pagination.limit * (pagination.current - 2);
          break;

        case "next":
          pagingConfig.offset = pagination.limit * pagination.current;
          break;

        case "last":
          pagingConfig.offset =
            pagination.max * pagination.limit - pagination.limit;
          break;
      }
    } else {
      pagingConfig.offset = (action - 1) * pagination.limit;
    }

    try {
      fetchMyTransaction({
        paging: true,
        ...pagingConfig,
      });
    } catch (error) {
      console.log(error);
    }
  };

  if (myLimit && Object.keys(myLimit).length > 0) {
    return (
      <Stack gap={{ xs: 2, lg: 3 }} className="w-full h-full py-6">
        <BizPayLater />
        {myTransactions && myTransactions.length > 0 ? (
          <BillingTable />
        ) : (
          <Stack alignItems={"center"} p={3}>
            <p className="text-2xl font-semibold">
              {t("finance.activatedLanding.noBilling")}
            </p>
            <p className="text-sm font-normal">
              {t("finance.activatedLanding.noBillingText")}
            </p>
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <div className="w-full h-full">
      <Stack
        direction={{ xs: "column-reverse", lg: "row" }}
        spacing={4}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        <div>
          <Typography level="h2" gutterBottom>
            {t("finance.landing.title")}
          </Typography>
          <Typography level="body-lg" gutterBottom>
            {t("finance.landing.subtitle")}
          </Typography>

          <Link
            to={`${BrowserRouterConstants.ContactUs}?text=${t(
              "dashboard.contactUsText"
            )}`}
            target="_blank"
            className="bg-[#499873] px-4 py-[10px] rounded-lg text-white text-center text-sm font-semibold sm:inline-block block mt-2"
          >
            {t("finance.landing.contactUs")}
          </Link>
        </div>
        <img src="/images/BNPL/cover.png" className="max-h-[400px]" />
      </Stack>
      <Divider />
      <div className="my-5">
        <Typography level="h3" gutterBottom>
          {t("finance.landing.title")}
        </Typography>
        <Stack
          direction={{ lg: "row", sm: "column" }}
          gap={{ sm: 16, xs: 4 }}
          sx={{
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <Stack direction={{ xs: "row", sm: "column" }} gap={{ xs: 2 }}>
            <img
              src="/images/BNPL/feat_1.png"
              alt="feature one"
              className="w-[72px] mb-3"
            />
            <div>
              <Typography level="title-md" gutterBottom>
                {t("finance.landing.feat_1_title")}
              </Typography>
              <Typography level="body-sm" gutterBottom>
                {t("finance.landing.feat_1_description")}
              </Typography>
            </div>
          </Stack>
          <Stack direction={{ xs: "row", sm: "column" }} gap={{ xs: 2 }}>
            <img
              src="/images/BNPL/feat_2.png"
              alt="feature one"
              className="w-[72px] mb-3"
            />
            <div>
              <Typography level="title-md" gutterBottom>
                {t("finance.landing.feat_2_title")}
              </Typography>
              <Typography level="body-sm" gutterBottom>
                {t("finance.landing.feat_2_description")}
              </Typography>
            </div>
          </Stack>
          <Stack direction={{ xs: "row", sm: "column" }} gap={{ xs: 2 }}>
            <img
              src="/images/BNPL/feat_3.png"
              alt="feature one"
              className="w-[72px] mb-3"
            />
            <div>
              <Typography level="title-md" gutterBottom>
                {t("finance.landing.feat_3_title")}
              </Typography>
              <Typography level="body-sm" gutterBottom>
                {t("finance.landing.feat_3_description")}
              </Typography>
            </div>
          </Stack>
        </Stack>
      </div>

      <Divider />

      <div className="my-5">
        <Typography level="h3" gutterBottom fontSize={{ xs: 20, sm: 24 }}>
          {t("finance.landing.unlock_title")}
        </Typography>
        <Stack
          direction={{ lg: "row", sm: "column" }}
          gap={{ sm: 4, xs: 1 }}
          sx={{
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <Stack
            sx={{
              flex: 1,
            }}
          >
            <Typography level="title-md" gutterBottom>
              {t("finance.landing.feat_4_title")}
            </Typography>
            <Typography level="body-sm" gutterBottom>
              {t("finance.landing.feat_4_description")}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flex: 1,
            }}
          >
            <Typography level="title-md" gutterBottom>
              {t("finance.landing.feat_5_title")}
            </Typography>
            <Typography level="body-sm" gutterBottom>
              {t("finance.landing.feat_5_description")}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flex: 1,
            }}
          >
            <Typography level="title-md" gutterBottom>
              {t("finance.landing.feat_6_title")}
            </Typography>
            <Typography level="body-sm" gutterBottom>
              {t("finance.landing.feat_6_description")}
            </Typography>
          </Stack>
        </Stack>
      </div>
    </div>
  );
}
